/**
 * JobDetail Page (US-044: Application Sorting, US-047: De-emphasize Coordinators,
 *                 US-051: Collapsible Job Settings, US-052: Sortable Applications Table,
 *                 US-053: Clarify Sync vs Backfill UX)
 *
 * Displays job details and a sortable table of applications.
 *
 * Page Layout (US-051, US-052):
 * 1. Header with navigation and job reference
 * 2. Job Header Card (title, status, basic info)
 * 3. Applications Table (PRIMARY CONTENT - emphasized with larger header)
 * 4. Hiring Team Section (US-047: de-emphasized, compact inline display)
 * 5. Job Settings (collapsible, default collapsed) - contains 5 settings:
 *    - AI Processing Stages
 *    - Review Sync Settings
 *    - Sync New Candidates (imports from SmartRecruiters)
 *    - Review Unprocessed Candidates (queues unreviewed candidates for AI)
 *    - Status Controls
 *
 * Sync vs Backfill Clarification (US-053):
 * - "Sync New Candidates" - Imports candidates from SmartRecruiters NOT in local database
 * - "Review Unprocessed Candidates" - Processes candidates WITHOUT AI reviews in local database
 * - Both features have distinct visual styling and help tooltips
 * - Review Unprocessed Candidates shows warning when no unreviewed candidates exist
 *
 * Collapsible Features (US-051):
 * - Smooth expand/collapse animation with CSS transitions
 * - Badge showing "8 settings" to indicate section contents
 * - Collapse state persisted in localStorage per job
 * - Default collapsed to prioritize applications view
 *
 * Sorting Features (US-052):
 * - Client-side sortable columns: Candidate Name, Status, Stage, Date Applied
 * - Default sort: Applied (newest first)
 * - Sort icons: ChevronUp/ChevronDown for active column, ChevronsUpDown for inactive
 * - Handles edge cases: null values sorted to bottom
 */

import { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useJob, useUpdateJob, useDeleteJob, useTriggerJobSync, useToggleJobSync, useBackfillPreview, useStartBackfill, useNotificationSettings, useUpdateNotificationSettings, useRefreshHiringTeam, useSourcingMatches, useUpdateSourcingSettings } from '../hooks/useApi';
import SuggestedCandidates from '../components/sourcing/SuggestedCandidates';
import JobAccuracySection from '../components/JobAccuracySection';
import {
  Briefcase,
  MapPin,
  ArrowLeft,
  ExternalLink,
  RefreshCw,
  Users,
  Calendar,
  Clock,
  Building2,
  Settings,
  Cloud,
  CloudOff,
  HelpCircle,
  PlayCircle,
  CheckCircle2,
  AlertCircle,
  Archive,
  Trash2,
  UserCheck,
  Bell,
  BellOff,
  Download,
  Sparkles,
  Target,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { JobStatusBadge } from '../components/StatusBadge';
import { RetentionWarningBanner } from '../components/retention';
import { SkeletonDetail } from '../components/ui/Skeleton';
import { useToast } from '../contexts/ToastContext';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { JobCriteriaEditor } from '../components/JobCriteriaEditor';
import { SponsorshipPolicyControl } from '../components/SponsorshipPolicyControl';

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const jobId = parseInt(id || '0', 10);
  const { showToast } = useToast();

  const { data: job, isLoading, error } = useJob(jobId);
  const updateJobMutation = useUpdateJob();
  const deleteJobMutation = useDeleteJob();
  const syncMutation = useTriggerJobSync();
  const toggleSyncMutation = useToggleJobSync();

  // US-053: Backfill hooks for Process Missed Reviews feature
  const { data: backfillPreview } = useBackfillPreview(jobId);
  const backfillMutation = useStartBackfill();

  // Notification settings hooks
  const { data: notifSettings } = useNotificationSettings(jobId);
  const updateNotifMutation = useUpdateNotificationSettings();
  const refreshTeamMutation = useRefreshHiringTeam();

  // US-278: Suggested candidates tab
  const [activeTab, setActiveTab] = useState<'applications' | 'suggested'>('applications');
  const showSuggestedTab = job !== undefined;
  const { data: sourcingData } = useSourcingMatches(job?.passiveSourcingEnabled ? jobId : 0, { limit: 1 });
  const suggestedCount = sourcingData?.total ?? 0;
  const updateSourcingMutation = useUpdateSourcingSettings();

  const handleToggleActive = async () => {
    if (!job) return;

    const newStatus = job.status === 'active' ? 'inactive' : 'active';
    try {
      await updateJobMutation.mutateAsync({
        id: jobId,
        data: { status: newStatus },
      });
    } catch (err) {
      console.error('Failed to update job status:', err);
    }
  };

  const handleSaveAllowedStages = async (id: number, stages: string[]) => {
    await updateJobMutation.mutateAsync({
      id,
      data: { allowedStages: stages },
    });
  };

  const handleSync = async () => {
    try {
      await syncMutation.mutateAsync(jobId);
      showToast('Sync job queued successfully', 'success');
    } catch (err) {
      const error = err as Error;
      showToast(`Failed to sync: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleToggleSync = async () => {
    if (!job) return;
    try {
      const newEnabled = !job.syncEnabled;
      await toggleSyncMutation.mutateAsync({ jobId, enabled: newEnabled });
      showToast(
        `Review sync ${newEnabled ? 'enabled' : 'disabled'} for this job`,
        'success'
      );
    } catch (err) {
      const error = err as Error;
      showToast(error.message || 'Failed to toggle sync', 'error');
    }
  };

  const handleToggleSkipHumanReviewed = async () => {
    if (!job) return;
    try {
      const newValue = !job.skipHumanReviewed;
      await updateJobMutation.mutateAsync({
        id: jobId,
        data: { skipHumanReviewed: newValue },
      });
      showToast(
        newValue
          ? 'Human-reviewed candidates will be skipped'
          : 'All candidates will receive AI review',
        'success'
      );
    } catch (err) {
      const error = err as Error;
      showToast(error.message || 'Failed to update setting', 'error');
    }
  };

  /**
   * US-053: Start processing missed reviews (backfill)
   * Queues unreviewed candidates for AI processing
   */
  const handleStartBackfill = async () => {
    try {
      const result = await backfillMutation.mutateAsync(jobId);
      showToast(`Processing started: ${result.queuedCount} candidates queued for review`, 'success');
    } catch (err) {
      const error = err as Error;
      showToast(`Failed to start processing: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleArchiveJob = async () => {
    if (!window.confirm('Archive this job? It will be set to inactive status.')) return;
    try {
      await updateJobMutation.mutateAsync({ id: jobId, data: { status: 'inactive' } });
      showToast('Job archived successfully', 'success');
      navigate('/jobs');
    } catch (err) {
      const error = err as Error;
      showToast(`Archive failed: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleDeleteJob = async () => {
    if (!job) return;
    const isActive = job.status === 'active';
    const msg = isActive
      ? `"${job.title}" is active. Archive it first, then permanently delete it and all its applications and reviews?\n\nThis cannot be undone.`
      : `Permanently delete "${job.title}" and all its applications and reviews?\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      if (isActive) {
        await updateJobMutation.mutateAsync({ id: jobId, data: { status: 'inactive' } });
      }
      await deleteJobMutation.mutateAsync(jobId);
      showToast('Job deleted', 'success');
      navigate('/jobs');
    } catch (err) {
      const error = err as Error;
      showToast(`Delete failed: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleToggleNotify = async (memberId: number, currentValue: boolean) => {
    try {
      await updateNotifMutation.mutateAsync({
        jobId,
        members: [{ id: memberId, notifyOnReview: !currentValue }],
      });
      showToast(
        !currentValue ? 'Member will receive review notifications' : 'Member removed from notifications',
        'success'
      );
    } catch (err) {
      const error = err as Error;
      showToast(error.message || 'Failed to update notification setting', 'error');
    }
  };

  const handleRefreshTeam = async () => {
    try {
      await refreshTeamMutation.mutateAsync(jobId);
      showToast('Hiring team refreshed from SmartRecruiters', 'success');
    } catch (err) {
      const error = err as Error;
      showToast(error.message || 'Failed to refresh hiring team', 'error');
    }
  };

  // US-071: Calculate days until retention deletion for inactive jobs
  // Must be before early returns to satisfy Rules of Hooks
  const retentionDaysRemaining = useMemo(() => {
    if (!job || job.status !== 'inactive' || !job.updatedAt) return null;
    const retentionDays = 365;
    const updatedAt = new Date(job.updatedAt);
    const deletionDate = new Date(updatedAt.getTime() + retentionDays * 24 * 60 * 60 * 1000);
    const daysLeft = Math.ceil((deletionDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
    return daysLeft <= 30 ? daysLeft : null;
  }, [job?.status, job?.updatedAt]);

  if (isLoading) {
    return (
      <SkeletonDetail showSidebar={false} sections={2} />
    );
  }

  if (error || !job) {
    return (
      <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">
            Error loading job: {error?.message || 'Job not found'}
          </p>
          <button onClick={() => navigate('/jobs')} className="btn btn-primary mt-4">
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  // Parse allowed stages if stored as JSON string, otherwise use empty array
  // US-050: Default to ['NEW'] when no stages are saved
  const allowedStages = (() => {
    try {
      if (typeof job.status === 'string' && job.status.startsWith('[')) {
        const parsed = JSON.parse(job.status);
        return parsed.length > 0 ? parsed : ['NEW'];
      }
      if ('allowedStages' in job && Array.isArray((job as any).allowedStages)) {
        const stages = (job as any).allowedStages;
        return stages.length > 0 ? stages : ['NEW'];
      }
      return ['NEW'];
    } catch {
      return ['NEW'];
    }
  })();

  return (
    <div className="space-y-6">
      {/* US-071: Retention warning for jobs approaching deletion */}
      {retentionDaysRemaining !== null && (
        <RetentionWarningBanner
          daysRemaining={retentionDaysRemaining}
          entityType="job"
          entityName={job.title}
          dismissible
          settingsLink="/data-retention"
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/jobs')}
          className="rounded-lg p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Job Details</h1>
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            {job.refNumber && (
              <span className="font-medium text-gray-700 dark:text-gray-300">Ref# {job.refNumber}</span>
            )}
            <span>ID: {job.srJobId}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/jobs/${jobId}/funnel`}
            className="btn btn-secondary flex items-center gap-1.5"
          >
            ⊞ Funnel View
          </Link>
          {job.status === 'active' && (
            <button
              onClick={handleArchiveJob}
              className="btn btn-secondary text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950"
              disabled={updateJobMutation.isPending}
            >
              <Archive className="mr-2 h-4 w-4" />
              Archive Job
            </button>
          )}
          <button
            onClick={handleDeleteJob}
            className="btn btn-secondary text-red-600 dark:text-red-400 border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-950"
            disabled={deleteJobMutation.isPending || updateJobMutation.isPending}
            title="Permanently delete this job and all its data"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Job
          </button>
          <a
            href={`https://www.smartrecruiters.com/app/jobs/details/${job.srJobId}/people`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            View in SmartRecruiters
          </a>
        </div>
      </div>

      {/* US-052: Job Header Card - Always visible, contains job title and key metadata */}
      <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
        {/* Title and Basic Info */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/50">
              <Briefcase className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{job.title}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {job.refNumber && (
                  <span className="inline-flex items-center rounded bg-primary-50 dark:bg-primary-900/30 px-2 py-0.5 text-sm font-medium text-primary-700 dark:text-primary-300">
                    Ref# {job.refNumber}
                  </span>
                )}
                {/* US-084: Data source badge - Internal Job vs External Posting */}
                {job.postingUuid ? (
                  <span
                    className="inline-flex items-center rounded bg-green-50 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300"
                    title={`Posting UUID: ${job.postingUuid}`}
                  >
                    Published Posting
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400">
                    Internal Job Only
                  </span>
                )}
                {job.department && (
                  <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <Building2 className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <span className="text-sm">{job.department}</span>
                  </div>
                )}
                {job.location && (
                  <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <MapPin className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <span className="text-sm">{job.location}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <JobStatusBadge status={job.status} />
        </div>

        {/* Metadata */}
        <div className="mt-6 grid grid-cols-1 gap-4 border-t border-gray-200 dark:border-gray-700 pt-6 md:grid-cols-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
              <Users className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Applications</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {job.applicationCount ?? 0}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
              <Calendar className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Created</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
              <Clock className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Last Synced</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {job.lastSyncedAt
                  ? formatDistanceToNow(new Date(job.lastSyncedAt), { addSuffix: true })
                  : 'Never'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* US-278: Tab bar — only shown when passive sourcing is enabled */}
      {showSuggestedTab && (
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'applications'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Applications
          </button>
          <button
            onClick={() => setActiveTab('suggested')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'suggested'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Suggested
            {suggestedCount > 0 && (
              <span className="bg-blue-500/20 text-blue-400 text-xs font-medium px-1.5 py-0.5 rounded-full">
                {suggestedCount}
              </span>
            )}
          </button>
        </div>
      )}

      {/* US-278: Tab content — Suggested candidates or Applications card */}
      {activeTab === 'suggested' && showSuggestedTab ? (
        <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
          <SuggestedCandidates
            jobId={jobId}
            passiveSourcingEnabled={job.passiveSourcingEnabled ?? false}
            sourcingMinStars={job.sourcingMinStars ?? 4}
          />
        </div>
      ) : (
        /* Applications — links to full Applications page filtered by this job */
        <div className="card dark:bg-gray-800 dark:border-gray-700 overflow-hidden ring-1 ring-primary-100 dark:ring-primary-900/50">
          <div className="bg-gradient-to-r from-primary-50 to-white dark:from-primary-900/30 dark:to-gray-800 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/50">
                  <Users className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Applications</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {job.applicationCount ?? 0} candidate{(job.applicationCount ?? 0) !== 1 ? 's' : ''} for this position
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {/* US-556: the task is conceived here but performed on the
                    Applications page (which owns the selection checkboxes) —
                    this is a link, not a feature. */}
                <Link
                  to={`/applications?status=all&jobId=${jobId}`}
                  className="text-sm text-primary-600 hover:underline dark:text-primary-400"
                >
                  Download resumes →
                </Link>
                <Link
                  to={`/applications?status=all&jobId=${jobId}`}
                  className="btn btn-primary"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Applications
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Review Accuracy */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 overflow-hidden">
        <CollapsibleSection
          title="AI Review Accuracy"
          icon={<Target className="h-5 w-5" />}
          defaultOpen={false}
          storageKey={`job-ai-accuracy-collapsed-${jobId}`}
        >
          <JobAccuracySection jobId={jobId} />
        </CollapsibleSection>
      </div>

      {/* US-047: Hiring Team Section - De-emphasized, collapsed by default, shows names not SR IDs
          Names (firstName + lastName) are primary display; email shown as secondary small text;
          SR User ID removed from display entirely; notifyOnReview badge shown when enabled */}
      {job.coordinators && job.coordinators.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 overflow-hidden">
          <CollapsibleSection
            title="Hiring Team"
            icon={<Users className="h-5 w-5" />}
            defaultOpen={false}
            storageKey={`job-hiring-team-collapsed-${jobId}`}
            badge={
              <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                {job.coordinators.length}
              </span>
            }
          >
            <div className="flex flex-wrap gap-2">
              {job.coordinators.map((coordinator) => {
                const displayName = [coordinator.firstName, coordinator.lastName].filter(Boolean).join(' ');
                const avatarChar = displayName ? displayName.charAt(0).toUpperCase() : coordinator.email.charAt(0).toUpperCase();
                return (
                  <div
                    key={coordinator.id}
                    className="inline-flex items-center gap-2 rounded-md bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 px-2.5 py-1.5 text-sm"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-600 text-xs font-medium text-gray-600 dark:text-gray-300 shrink-0">
                      {avatarChar}
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800 dark:text-gray-200 leading-tight">
                        {displayName || coordinator.email}
                      </p>
                      {displayName && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight truncate">
                          {coordinator.email}
                        </p>
                      )}
                    </div>
                    <span className="rounded bg-gray-100 dark:bg-gray-600 px-1.5 py-0.5 text-xs text-gray-500 dark:text-gray-400 shrink-0">
                      {coordinator.role}
                    </span>
                    {coordinator.notifyOnReview && (
                      <span className="rounded bg-primary-100 dark:bg-primary-900/30 px-1.5 py-0.5 text-xs text-primary-700 dark:text-primary-400 shrink-0" title="Receives review notifications">
                        Notify
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        </div>
      )}

      {/* US-051: Job Settings Section - Collapsible, default collapsed, lighter visual weight
          Contains: AI Processing Stages, Review Sync, Sync Candidates, Process Missed Reviews, Status Controls
          State persisted to localStorage for user preference retention
          Uses lighter card style to de-emphasize relative to Applications table */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 overflow-hidden">
        <CollapsibleSection
          title="Job Settings"
          icon={<Settings className="h-5 w-5" />}
          defaultOpen={false}
          storageKey={`job-settings-collapsed-${jobId}`}
          badge={
            <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
              10 settings
            </span>
          }
        >
          <div className="space-y-6">
            {/* US-048: AI Review Eligibility (renamed from AI Processing Stages) */}
            <div>
              <h4 className="mb-2 text-base font-medium text-gray-900 dark:text-gray-100">AI Review Eligibility</h4>
              <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                Select which application stages trigger AI resume review. Candidates in these stages will be automatically analyzed.
              </p>
              {/* US-049: Stage options - NEW, IN_REVIEW, INTERVIEW (OFFER and HIRED removed) */}
              <div className="space-y-3">
                {[
                  { value: 'NEW', label: 'New', description: 'Newly applied (recommended)' },
                  { value: 'IN_REVIEW', label: 'In Review', description: 'Being screened by recruiter' },
                  { value: 'INTERVIEW', label: 'Interview', description: 'In interview process' },
                ].map((stage) => (
                  <label key={stage.value} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowedStages.includes(stage.value)}
                      onChange={async () => {
                        const newStages = allowedStages.includes(stage.value)
                          ? allowedStages.filter((s: string) => s !== stage.value)
                          : [...allowedStages, stage.value];
                        // US-050: Require at least one stage
                        if (newStages.length === 0) {
                          showToast('At least one stage must be selected.', 'error');
                          return;
                        }
                        await handleSaveAllowedStages(jobId, newStages);
                      }}
                      disabled={updateJobMutation.isPending}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{stage.label}</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{stage.description}</p>
                    </div>
                  </label>
                ))}
              </div>
              {/* US-048: Helper text */}
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                Tip: NEW is the primary stage. Add IN_REVIEW or INTERVIEW to re-analyze candidates later in the process.
              </p>
            </div>

            {/* Screening Policy Layer (2026-06-03): per-job sponsorship policy */}
            <div>
              <SponsorshipPolicyControl
                jobId={jobId}
                initialValue={(job.sponsorshipOffered as 'yes' | 'no' | 'unspecified') ?? 'unspecified'}
              />
            </div>

            {/* Sync Configuration */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="mb-3 text-base font-medium text-gray-900 dark:text-gray-100">Sync Configuration</h4>

              {/* Review Sync Toggle */}
              <div className="mb-6 rounded-lg bg-gray-50 dark:bg-gray-800/50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {job.syncEnabled ? (
                      <Cloud className="h-5 w-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <CloudOff className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">Review Sync to SmartRecruiters</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {job.syncEnabled
                          ? 'AI reviews will be automatically synced back to SmartRecruiters'
                          : 'Review sync is disabled - reviews will not be posted to SmartRecruiters'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleSync}
                    disabled={toggleSyncMutation.isPending}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                      job.syncEnabled ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        job.syncEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Skip Human-Reviewed Candidates Toggle */}
              <div className="mb-6 rounded-lg bg-gray-50 dark:bg-gray-800/50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <UserCheck className={`h-5 w-5 ${job.skipHumanReviewed ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'}`} />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">Skip Human-Reviewed Candidates</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {job.skipHumanReviewed
                          ? 'Candidates with existing human reviews on SmartRecruiters will skip AI review'
                          : 'All candidates will receive AI review regardless of existing human reviews'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleSkipHumanReviewed}
                    disabled={updateJobMutation.isPending}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                      job.skipHumanReviewed ? 'bg-amber-500' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        job.skipHumanReviewed ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Passive Sourcing Toggle */}
              <div className="mb-6 rounded-lg bg-gray-50 dark:bg-gray-800/50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Sparkles className={`h-5 w-5 ${job.passiveSourcingEnabled ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">Passive Sourcing</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {job.passiveSourcingEnabled
                          ? 'Nightly scan finds existing candidates who match this job'
                          : 'Off — enable to surface suggested candidates from your existing talent pool'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await updateSourcingMutation.mutateAsync({
                          jobId,
                          data: { passiveSourcingEnabled: !job.passiveSourcingEnabled },
                        });
                        showToast(
                          !job.passiveSourcingEnabled ? 'Passive sourcing enabled' : 'Passive sourcing disabled',
                          'success'
                        );
                      } catch {
                        showToast('Failed to update passive sourcing', 'error');
                      }
                    }}
                    disabled={updateSourcingMutation.isPending}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                      job.passiveSourcingEnabled ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        job.passiveSourcingEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* US-053: Import New Applicants - Import from SmartRecruiters */}
              <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Download className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                      <p className="font-medium text-gray-900 dark:text-gray-100">Import New Applicants</p>
                      <span
                        className="cursor-help text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Finds applicants in SmartRecruiters that haven't been imported to Arbie yet and adds them to the local database."
                      >
                        <HelpCircle className="h-4 w-4" />
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      Import new applicants from SmartRecruiters who aren't in Arbie yet. Runs automatically every hour — trigger manually to check immediately.
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                      Last synced:{' '}
                      {job.lastSyncedAt
                        ? format(new Date(job.lastSyncedAt), 'PPpp')
                        : 'Never'}
                    </p>
                  </div>
                  <button
                    onClick={handleSync}
                    disabled={syncMutation.isPending || job.status === 'inactive'}
                    title={job.status === 'inactive' ? 'Cannot sync inactive job. Reactivate the job first.' : undefined}
                    className="btn btn-primary ml-4"
                  >
                    <RefreshCw
                      className={`mr-2 h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`}
                    />
                    {syncMutation.isPending ? 'Syncing...' : 'Sync Now'}
                  </button>
                </div>
              </div>

              {/* US-053: Review Unprocessed Candidates - Queue candidates missing AI reviews */}
              <div className="mt-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                      <p className="font-medium text-gray-900 dark:text-gray-100">Review Unprocessed Candidates</p>
                      <span
                        className="cursor-help text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                        title="This looks at candidates already in Arbie's database and queues any who haven't received an AI review yet."
                      >
                        <HelpCircle className="h-4 w-4" />
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      Generate AI reviews for candidates who haven't been analyzed yet. Use this when Arbie was added to a job after candidates already applied, or if some reviews failed.
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                      Note: 'Import New Applicants' finds candidates not yet in Arbie. This finds existing candidates missing AI reviews.
                    </p>
                    {/* Status indicator based on backfill preview */}
                    {backfillPreview && (
                      <div className="mt-2">
                        {backfillPreview.unreviewedCount === 0 ? (
                          <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              All candidates have reviews - nothing to process
                            </span>
                          </div>
                        ) : backfillPreview.backfillInProgress ? (
                          <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              Processing in progress...
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              {backfillPreview.unreviewedCount} candidate{backfillPreview.unreviewedCount !== 1 ? 's' : ''} without reviews
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleStartBackfill}
                    disabled={
                      backfillMutation.isPending ||
                      backfillPreview?.backfillInProgress ||
                      backfillPreview?.unreviewedCount === 0 ||
                      job.status === 'inactive'
                    }
                    title={job.status === 'inactive' ? 'Cannot process reviews for inactive job. Reactivate the job first.' : undefined}
                    className="btn btn-secondary ml-4 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    {backfillMutation.isPending ? 'Starting...' : 'Process Now'}
                  </button>
                </div>
              </div>
            </div>

            {/* Screening Criteria */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <JobCriteriaEditor jobId={jobId} srJobId={job.srJobId} />
            </div>

            {/* Notification Recipients */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">Notification Recipients</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Control who receives email notifications when reviews are completed or feedback is submitted
                  </p>
                </div>
                <button
                  onClick={handleRefreshTeam}
                  disabled={refreshTeamMutation.isPending}
                  className="btn btn-secondary text-sm"
                >
                  <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshTeamMutation.isPending ? 'animate-spin' : ''}`} />
                  Refresh Team
                </button>
              </div>

              {notifSettings && notifSettings.members.length > 0 ? (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Member</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Role</th>
                        <th className="px-4 py-2.5 text-center text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Notify</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {notifSettings.members.map((member) => (
                        <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                          <td className="px-4 py-2.5">
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {[member.firstName, member.lastName].filter(Boolean).join(' ') || member.email}
                              </p>
                              {(member.firstName || member.lastName) && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="inline-flex rounded bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400">
                              {member.role}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <button
                              onClick={() => handleToggleNotify(member.id, member.notifyOnReview)}
                              disabled={updateNotifMutation.isPending}
                              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 ${
                                member.notifyOnReview ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                              }`}
                              title={member.notifyOnReview ? 'Receiving notifications' : 'Not receiving notifications'}
                            >
                              <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  member.notifyOnReview ? 'translate-x-4' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      <Bell className="inline h-3 w-3 mr-1" />
                      Admin ({notifSettings.adminEmail}) always receives a copy of all notifications
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center">
                  <BellOff className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    No hiring team synced yet
                  </p>
                  <button
                    onClick={handleRefreshTeam}
                    disabled={refreshTeamMutation.isPending}
                    className="btn btn-primary mt-3 text-sm"
                  >
                    <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshTeamMutation.isPending ? 'animate-spin' : ''}`} />
                    Refresh Team from SmartRecruiters
                  </button>
                </div>
              )}
            </div>

            {/* Status Controls */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="mb-3 text-base font-medium text-gray-900 dark:text-gray-100">Status Controls</h4>
              <div className="flex items-center justify-between rounded-lg bg-gray-50 dark:bg-gray-800/50 p-4">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">Job Status</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {job.status === 'active'
                      ? 'This job is currently open and accepting applications'
                      : 'This job is currently closed'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <JobStatusBadge status={job.status} />
                  {['FILLED', 'CANCELLED', 'ON_HOLD'].includes(job.srStatus || '') ? (
                    <button
                      onClick={handleToggleActive}
                      disabled={updateJobMutation.isPending}
                      className="btn btn-secondary"
                    >
                      {updateJobMutation.isPending ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Reopen Job
                    </button>
                  ) : job.status === 'active' ? (
                    <button
                      onClick={handleToggleActive}
                      disabled={updateJobMutation.isPending}
                      className="btn btn-secondary"
                    >
                      {updateJobMutation.isPending ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Close Job
                    </button>
                  ) : job.status === 'inactive' ? (
                    <button
                      onClick={handleToggleActive}
                      disabled={updateJobMutation.isPending}
                      className="btn btn-secondary"
                    >
                      {updateJobMutation.isPending ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Re-open Job
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}
